import React, { useState, useEffect } from "react";
import { Users, MapPin } from "lucide-react";
import { OrderStats, User, CountryInfo, ShippingCompany } from "./types";
import { API_BASE_URL } from "../../utils/apiConfig";

interface CountryStatsProps {
  stats: OrderStats | null;
  currentUser?: User;
}

interface ShippingCompanyData {
  id: string;
  name: string;
  countries: string[];
  status: string;
  endpoints: string[];
}

const CountryStats: React.FC<CountryStatsProps> = ({ stats, currentUser }) => {
  const [shippingCompanies, setShippingCompanies] = useState<
    ShippingCompanyData[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShippingCompanies();
  }, []);

  const fetchShippingCompanies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shipping/companies`);
      const data = await response.json();
      if (data.success) {
        setShippingCompanies(data.data.companies);
      }
    } catch (error) {
      console.error("Error fetching shipping companies:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTopCountries = (): CountryInfo[] => {
    if (!stats?.byCountry) return [];

    return Object.entries(stats.byCountry)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([country, count]) => ({ country: country.toUpperCase(), count }));
  };

  const getShippingCompanyForCountry = (
    countryCode: string
  ): ShippingCompany => {
    const company = shippingCompanies.find((c) =>
      c.countries.includes(countryCode.toUpperCase())
    );

    if (company) {
      return {
        name: company.name,
        status: company.status,
        color:
          company.status === "connected"
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800",
      };
    }

    return {
      name: "Not configured",
      status: "disconnected",
      color: "bg-red-100 text-red-800",
    };
  };

  if (currentUser?.role === "country-post-manager") {
    // Shipping companies status for post office managers
    // Filter companies to only show those serving the user's assigned countries
    const userCountries = currentUser.countries.map((c) => c.toUpperCase());
    const filteredCompanies = shippingCompanies.filter((company) =>
      company.countries.some((country) => userCountries.includes(country))
    );

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-green-600" />
          Shipping Companies Status
        </h3>
        {loading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : filteredCompanies.length === 0 ? (
          <div className="text-sm text-gray-500">
            No shipping companies configured for your countries
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCompanies.map((company) => {
              const statusColor =
                company.status === "connected"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800";

              return (
                <div
                  key={company.id}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">
                      {company.name} ({company.countries.join(", ")})
                    </span>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}
                  >
                    {company.status === "connected"
                      ? "Connected"
                      : "Not Connected"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Top countries for other roles
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
        <Users className="w-5 h-5 mr-2 text-green-600" />
        Top Countries
      </h3>
      <div className="space-y-3">
        {getTopCountries().map(({ country, count }) => (
          <div key={country} className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">{country}</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CountryStats;
